import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { getPublicTournament, getPublicLeaderboard, subscribeTournamentStream } from '../api/publicApi';
import { Spinner, PageError } from '../components/UI';
import Leaderboard from '../components/tournament/Leaderboard';
import RoundCard from '../components/tournament/RoundCard';
import { StatusBadge } from '../components/tournament/TournamentBadges';
import { IconTrophy } from '../components/tournament/TournamentIcons';
import { FORMAT_LABELS, SPORT_LABELS } from '../components/tournament/tournamentConstants';

// Задержка переподключения SSE при ошибке (мс)
const SSE_RECONNECT_DELAY_MS = 3_000;

const PublicTournamentPage = () => {
  const { token } = useParams();

  const [tournament,  setTournament]  = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [stage1Leaderboard, setStage1Leaderboard] = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [error,       setError]       = useState(null);

  const esRef          = useRef(null);
  const reconnectTimer = useRef(null);
  // Актуальный tournament в ref — читается из замыканий SSE и visibilitychange
  // без необходимости пересоздавать коллбэки при каждом обновлении стейта.
  const tournamentRef  = useRef(null);
  tournamentRef.current = tournament;

  // Beat the Box: на этапе 2 подгружаем групповую таблицу этапа 1 (свёрнутая история).
  const publicStage2Active = !!tournament
    && tournament.format === 'BEAT_THE_BOX'
    && (tournament.rounds || []).some(r => r.roundNumber > 3);

  useEffect(() => {
    if (!publicStage2Active) { setStage1Leaderboard([]); return; }
    getPublicLeaderboard(token, 1)
      .then(data => setStage1Leaderboard(data || []))
      .catch(() => setStage1Leaderboard([]));
  }, [publicStage2Active, token, tournament?.updatedAt]);

  const fetchAll = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const [t, lb] = await Promise.all([
        getPublicTournament(token),
        getPublicLeaderboard(token),
      ]);
      setTournament(t);
      setLeaderboard(lb || []);
      setError(null);
    } catch (err) {
      if (!silent) {
        setError(err.status === 404
          ? 'Турнир не найден или ссылка недействительна'
          : 'Не удалось загрузить турнир');
      }
    } finally {
      if (!silent) setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const connectSSE = useCallback(() => {
    if (esRef.current) {
      esRef.current.close();
    }

    esRef.current = subscribeTournamentStream(
      token,
      // onUpdate: бэкенд прислал полный снапшот
      ({ tournament: t, leaderboard: lb }) => {
        setTournament(t);
        setLeaderboard(lb || []);
      },
      // onError: SSE-соединение упало.
      // Не переподключаемся если страница скрыта (восстановим при visibilitychange)
      // или если турнир уже завершён (обновлений больше не будет).
      () => {
        esRef.current?.close();
        esRef.current = null;
        if (document.visibilityState === 'hidden') return;
        if (tournamentRef.current?.status !== 'RUNNING') return;
        reconnectTimer.current = setTimeout(connectSSE, SSE_RECONNECT_DELAY_MS);
      },
    );
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // Первичная загрузка данных (чтобы страница не ждала первого SSE-события)
  useEffect(() => {
    fetchAll();
  }, [token, fetchAll]);

  // SSE — только для RUNNING-турниров. Когда турнир переходит в FINISHED:
  // setTournament → status меняется → этот эффект пересчитывается → cleanup закрывает SSE.
  // Если турнир был FINISHED → стал RUNNING пока страница была скрыта:
  // visibilitychange вызовет fetchAll → tournament.status обновится → эффект переподключит SSE.
  useEffect(() => {
    if (!tournament || tournament.status !== 'RUNNING') return;
    connectSSE();
    return () => {
      clearTimeout(reconnectTimer.current);
      esRef.current?.close();
      esRef.current = null;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tournament?.status, token]);

  // При возврате на видимую страницу:
  // 1. Тихий рефетч — обновляет статус турнира; если стал RUNNING → SSE useEffect выше подключит.
  // 2. Если SSE упал пока страница была скрыта и турнир всё ещё RUNNING → переподключаем сразу.
  useEffect(() => {
    const handleVisibility = () => {
      if (document.visibilityState !== 'visible') return;
      fetchAll(true);
      if (tournamentRef.current?.status === 'RUNNING' && !esRef.current) {
        connectSSE();
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, [connectSSE, fetchAll]);

  // Поллинг при FINISHED — раз в минуту проверяем статус на случай возврата в работу.
  // Как только status снова станет RUNNING, SSE useEffect выше подключит подписку.
  useEffect(() => {
    if (tournament?.status !== 'FINISHED') return;
    const interval = setInterval(() => fetchAll(true), 60_000);
    return () => clearInterval(interval);
  }, [tournament?.status, fetchAll]);

  if (loading) return <Spinner text="Загрузка турнира..." />;
  if (error)   return <PageError message={error} onRetry={() => fetchAll()} />;
  if (!tournament) return null;

  const playerMap  = Object.fromEntries((tournament.players || []).map(p => [p.id, p]));
  const isFinished = tournament.status === 'FINISHED';

  return (
    <div style={{
      minHeight: '100vh',
      background: '#f8fafc',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    }}>
      {/* ── Шапка ─────────────────────────────────────────────────────────── */}
      <div style={{
        background: 'linear-gradient(135deg, #d97706 0%, #b45309 100%)',
        padding: '14px 16px 16px',
        color: '#fff',
        position: 'sticky',
        top: 0,
        zIndex: 10,
      }}>
        <div style={{ maxWidth: 480, margin: '0 auto' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            {/* Иконка + формат/спорт */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{
                width: 32, height: 32, borderRadius: 9,
                background: 'rgba(255,255,255,0.18)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <IconTrophy />
              </div>
              <div>
                <h1 style={{ fontSize: 17, fontWeight: 700, margin: 0 }}>
                  {FORMAT_LABELS[tournament.format] || tournament.format}
                </h1>
                {tournament.sport && (
                  <div style={{ fontSize: 12, opacity: 0.85, marginTop: 1 }}>
                    {SPORT_LABELS[tournament.sport] || tournament.sport}
                  </div>
                )}
              </div>
            </div>

            <StatusBadge status={tournament.status} />
          </div>
        </div>
      </div>

      {/* ── Контент ───────────────────────────────────────────────────────── */}
      <div style={{
        maxWidth: 480,
        margin: '0 auto',
        padding: '16px 16px 32px',
        display: 'flex',
        flexDirection: 'column',
        gap: 12,
      }}>

        {/* Баннер завершённого турнира */}
        {isFinished && (
          <div style={{
            display: 'flex', alignItems: 'center', gap: 10,
            padding: '12px 16px', borderRadius: 12,
            background: 'linear-gradient(135deg, #ede9fe, #ddd6fe)',
            border: '1.5px solid #c4b5fd',
          }}>
            <span style={{ fontSize: 22 }}>🏆</span>
            <div>
              <div style={{ fontSize: 14, fontWeight: 700, color: '#5b21b6' }}>
                Турнир завершён
              </div>
              <div style={{ fontSize: 12, color: '#7c3aed' }}>
                {(tournament.rounds || []).length} раундов сыграно
              </div>
            </div>
          </div>
        )}

        {/* Таблица лидеров (активный этап) */}
        <Leaderboard
          players={leaderboard}
          rankingMode={tournament.rankingMode}
          hasCompletedRound={(tournament.rounds || []).some(r => r.status === 'FINISHED')}
          title={publicStage2Active ? 'Таблица лидеров — этап 2' : 'Таблица лидеров'}
        />

        {/* История этапа 1 — свёрнутая панель, доступна во время этапа 2 */}
        {publicStage2Active && stage1Leaderboard.length > 0 && (
          <Leaderboard
            players={stage1Leaderboard}
            rankingMode={tournament.rankingMode}
            hasCompletedRound
            title="Этап 1 — группы (история)"
            collapsible
            defaultCollapsed
          />
        )}

        {/* Раунды */}
        {(tournament.rounds || []).length === 0 ? (
          <div style={{
            background: '#fff', borderRadius: 14, padding: 20,
            border: '1.5px solid #f0f0f5',
            textAlign: 'center', color: '#9ca3af', fontSize: 13,
          }}>
            Раунды ещё не начались
          </div>
        ) : (
          (tournament.rounds || []).map(round => (
            <React.Fragment key={round.id}>
              {/* Плашка Beat the Box: перед первым раундом этапа 1 */}
              {tournament.format === 'BEAT_THE_BOX' && round.roundNumber === 1 && (
                <div style={{
                  display: 'flex', alignItems: 'flex-start', gap: 10,
                  padding: '11px 14px', borderRadius: 12,
                  background: '#f0f9ff', border: '1.5px solid #bae6fd',
                }}>
                  <span style={{ fontSize: 16, flexShrink: 0, marginTop: 1 }}>ℹ️</span>
                  <div style={{ fontSize: 13, color: '#0369a1', lineHeight: 1.45 }}>
                    <strong>Beat the Box:</strong>{' '}
                    После завершения этапа 1 (раунды 1–3) группы будут пересобраны по результатам.
                  </div>
                </div>
              )}
              {/* Плашка Beat the Box: перед первым раундом этапа 2 (roundNumber > 3) */}
              {tournament.format === 'BEAT_THE_BOX' && round.roundNumber === 4 && (
                <div style={{
                  display: 'flex', alignItems: 'flex-start', gap: 10,
                  padding: '11px 14px', borderRadius: 12,
                  background: '#f0fdf4', border: '1.5px solid #86efac',
                }}>
                  <span style={{ fontSize: 16, flexShrink: 0, marginTop: 1 }}>ℹ️</span>
                  <div style={{ fontSize: 13, color: '#15803d', lineHeight: 1.45 }}>
                    <strong>Beat the Box:</strong>{' '}
                    Этап 2 — Gold / Silver / Bronze / Crystal. Итог определяет лучший игрок группы Gold.
                  </div>
                </div>
              )}
              <RoundCard
                round={round}
                playerMap={playerMap}
                canEdit={false}
                publicId={tournament.publicId}
                tournament={tournament}
                onScoreSaved={() => {}}
                tournamentFinished={isFinished}
              />
            </React.Fragment>
          ))
        )}

        {/* Подсказка об авто-обновлении */}
        <div style={{ textAlign: 'center', fontSize: 11, color: '#d1d5db', marginTop: 4 }}>
          Обновляется в реальном времени
        </div>
      </div>
    </div>
  );
};

export default PublicTournamentPage;
